/**
 * StepKnowledge — step 2 of the agent-creation flow: teach the agent.
 *
 * Website pages (hidden for Webflow-hosted agents) work like the Knowledge
 * Base's website links: find pages from ONE url (site or sitemap; the KB's own
 * `useLinkDiscovery` hook does the discovery) or enter urls manually. Nothing
 * runs until the owner clicks. Documents, Google Drive, OneDrive and Notion
 * open the portal's upload / picker modals (portaled to <body>).
 *
 * Limits: same numbers as the Knowledge Base (`knowledgeUsage`). A selection
 * larger than the remaining slots opens the portal's KnowledgeUpgradeModal (the
 * Studio / KB limit modal) with an extra "add the first N only" action. Upgrade
 * opens the plan modal (the billing page's PricingPlans →
 * Stripe → back to THIS step). Before the redirect we stash the found pages +
 * selection in sessionStorage and restore them on return, so after the plan
 * is active the owner adds the same pages with one click.
 *
 * Ingest runs in the background: the knowledge_ids we start are handed to
 * the page's training tracker; its strips render at the end of the body.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { TextField, Checkbox } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagnifyingGlass, faPlus, faTrash, faChevronDown, faGlobe, faFileLines, faCheck,
  faLightbulb, faCircleCheck, faRotateLeft,
} from '@fortawesome/free-solid-svg-icons';
import useLinkDiscovery from '../../../hooks/useLinkDiscovery.js';
import { isUrlTrainingHidden } from '../../../utils/webflowGate';
import UploadFileModal from '../../../components/uploadFileModal.js';
import GoogleDrivePickerModal from '../../../components/GoogleDrivePickerModal.js';
import OneDrivePickerModal from '../../../components/OneDrivePickerModal.js';
import NotionPickerModal from '../../../components/NotionPickerModal.js';
import KnowledgeUpgradeModal from '../../../components/knowledgeUpgradeModal.js';
import KnowledgeWarningModal from '../../../components/knowledgeWarningModal.js';
import { KnowledgeUsageBar } from '../../sectionPages.js';
import { API, FIELD_SX, knowledgeUsage, planName } from '../steps.js';
import PlanModal from '../PlanModal.js';
import StepFooter from './StepFooter.js';
import './StepKnowledge.css';

const CHECK_SX = { color: '#b9b5c9', '&.Mui-checked': { color: '#5616ea' }, '&.MuiCheckbox-indeterminate': { color: '#5616ea' }, padding: '4px' };

const stashKey = (botId) => `ac:kb:${botId}`;

const SOURCES = [
  { key: 'documents', type: 'document',     labelKey: 'ac_src_documents', icon: faFileLines },
  { key: 'gdrive',    type: 'google_drive', labelKey: 'ac_src_gdrive',    logo: '/google-drive.png' },
  { key: 'onedrive',  type: 'onedrive',     labelKey: 'ac_src_onedrive',  logo: '/onedrive.png' },
  { key: 'notion',    type: 'notion',       labelKey: 'ac_src_notion',    logo: '/notion-logo.png' },
];

/* Radar shown while pages are being discovered. */
function Radar({ text }) {
  return (
    <div className="ac-radar-wrap">
      <div className="ac-radar" aria-hidden="true">
        <span className="ac-radar-ring" /><span className="ac-radar-ring r2" /><span className="ac-radar-ring r3" />
        <span className="ac-radar-sweep" />
        <span className="ac-radar-core"><FontAwesomeIcon icon={faGlobe} /></span>
      </div>
      <div className="ac-radar-status"><span className="ac-radar-pulse" /><span key={text} className="ac-radar-text">{text}</span></div>
    </div>
  );
}

export default function StepKnowledge({ agent, botId, refreshAgent, goNext, goBack, allowBack = false, isWebflow, track, notify, returnPath, strips, prefillUrl = '' }) {
  const { t } = useTranslation();
  // isWebflow also drives the Channels step's platform preselect, so the prop
  // stays as-is; only the HIDING of website sources goes through the gate
  // (always false while it is disabled, utils/webflowGate.js).
  const hideWebsiteSources = isUrlTrainingHidden(isWebflow);
  const userId = localStorage.getItem('user_id');
  const usage = useMemo(() => knowledgeUsage(agent), [agent]);
  const { items, count, limit, atLimit, remaining } = usage;
  const plan = planName(agent);

  const [mode, setMode] = useState('find');
  const [fetchUrl, setFetchUrl] = useState(prefillUrl);
  const [manualUrls, setManualUrls] = useState(['']);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [planOpen, setPlanOpen] = useState(false);
  // Limit modal: { kind: 'pages', urls, fromDiscovery } | { kind: 'files', urls } | { kind: 'source' } (a tile clicked at 0 left)
  const [limitModal, setLimitModal] = useState(null);
  const [picker, setPicker] = useState(null);         // 'documents' | 'gdrive' | 'onedrive' | 'notion'
  const [noKnowledgeOpen, setNoKnowledgeOpen] = useState(false); // Continue with nothing added yet
  const [scanStep, setScanStep] = useState(0);
  const [justAdded, setJustAdded] = useState(0);
  const [listOpen, setListOpen] = useState(true);
  // The "new pages found" list + selection are owned here (seeded from the
  // discovery hook, or restored from the stash after a Stripe round-trip).
  const [pages, setPages] = useState([]);
  const [sel, setSel] = useState([]);
  const [restored, setRestored] = useState(false);

  const existingLinks = useMemo(() => items.filter((k) => k.type === 'url_link'), [items]);
  const countByType = useMemo(() => {
    const m = {};
    items.forEach((k) => { if (k.status !== 'failed') m[k.type] = (m[k.type] || 0) + 1; });
    return m;
  }, [items]);

  const discovery = useLinkDiscovery({ botId, existingLinks, autoExpandFirst: false });
  const { isFetching, errorMsg, newLinks, hasResult } = discovery;

  // A fresh discovery result replaces the list (everything preselected).
  useEffect(() => {
    if (!hasResult) return;
    setPages(newLinks);
    setSel(newLinks);
    setRestored(false);
  }, [hasResult, newLinks]);

  // Restore after a plan round-trip (stashed right before opening the plan modal).
  useEffect(() => {
    let raw = null;
    try { raw = sessionStorage.getItem(stashKey(botId)); } catch { /* noop */ }
    if (!raw) return;
    try { sessionStorage.removeItem(stashKey(botId)); } catch { /* noop */ }
    try {
      const st = JSON.parse(raw);
      if (st.mode) setMode(st.mode);
      if (st.fetchUrl) setFetchUrl(st.fetchUrl);
      if (Array.isArray(st.manualUrls) && st.manualUrls.length) setManualUrls(st.manualUrls);
      if (Array.isArray(st.pages) && st.pages.length) { setPages(st.pages); setSel(Array.isArray(st.sel) ? st.sel : st.pages); setListOpen(true); setRestored(true); }
    } catch { /* ignore a broken stash */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botId]);

  useEffect(() => {
    if (!isFetching) { setScanStep(0); return undefined; }
    const id = setInterval(() => setScanStep((s) => (s + 1) % 3), 1400);
    return () => clearInterval(id);
  }, [isFetching]);

  // Back from a connector OAuth round-trip: the picker asked to be reopened.
  useEffect(() => {
    const pending = localStorage.getItem('pendingOpenPicker');
    if (!pending) return;
    localStorage.removeItem('pendingOpenPicker');
    localStorage.removeItem('pendingKnowledgeTab');
    const map = { googledrive: 'gdrive', onedrive: 'onedrive', notion: 'notion' };
    if (map[pending]) setTimeout(() => setPicker(map[pending]), 350);
  }, []);

  // Open the plan modal; stash the current page list first, so a Stripe
  // round-trip brings the owner back to exactly this selection.
  const openUpgrade = useCallback(() => {
    setPicker(null); setLimitModal(null);
    try { sessionStorage.setItem(stashKey(botId), JSON.stringify({ mode, fetchUrl, manualUrls, pages, sel })); } catch { /* noop */ }
    setPlanOpen(true);
  }, [botId, mode, fetchUrl, manualUrls, pages, sel]);
  const closePlan = useCallback(() => {
    // Closed without a redirect: nothing to restore later.
    try { sessionStorage.removeItem(stashKey(botId)); } catch { /* noop */ }
    setPlanOpen(false);
  }, [botId]);

  const doAddUrls = useCallback(async (merged, fromDiscovery) => {
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}scrape_link`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ bot_id: botId, user_id: userId, urls: merged }) });
      if (!r.ok) throw new Error(`scrape_link ${r.status}`);
      const j = await r.json().catch(() => ({}));
      track(j.knowledge_ids || [], 'website');
      setJustAdded(merged.length);
      if (fromDiscovery) { discovery.reset(); setPages([]); setSel([]); setRestored(false); } else setManualUrls(['']);
      notify(t('ac_kb_added_pages', { count: merged.length }));
      await refreshAgent();
    } catch (e) { console.error(e); setErr(t('ac_kb_failed')); }
    finally { setBusy(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botId, userId, track, refreshAgent, notify, t]);

  const addUrls = useCallback((urls, { fromDiscovery = false } = {}) => {
    const merged = Array.from(new Set(urls.map((u) => u.trim()).filter(Boolean)));
    if (!merged.length) return;
    if (merged.length > remaining) { setLimitModal({ kind: 'pages', urls: merged, fromDiscovery }); return; }
    doAddUrls(merged, fromDiscovery);
  }, [remaining, doAddUrls]);

  const doAddDocuments = useCallback(async (fileUrls) => {
    if (!fileUrls?.length) return;
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}ingest_document_urls`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ bot_id: botId, document_urls: fileUrls, user_id: userId }) });
      if (!r.ok) throw new Error(`ingest ${r.status}`);
      const j = await r.json().catch(() => ({}));
      track(j.knowledge_ids || [], 'documents');
      notify(t('ac_kb_added_docs', { count: fileUrls.length }));
      await refreshAgent();
    } catch (e) { console.error(e); setErr(t('ac_kb_failed')); }
    finally { setBusy(false); }
  }, [botId, userId, track, refreshAgent, notify, t]);

  // The upload modal keeps its own per-batch cap; the PLAN limit is ours, so it
  // runs through the same limit modal as pages (upgrade · add the first N only).
  const addDocuments = useCallback((fileUrls) => {
    if (!fileUrls?.length) return;
    if (fileUrls.length > remaining) { setLimitModal({ kind: 'files', urls: fileUrls }); return; }
    doAddDocuments(fileUrls);
  }, [remaining, doAddDocuments]);

  // A source tile with nothing left on the plan → limit modal instead of the picker.
  const openSource = useCallback((key) => {
    if (remaining <= 0) { setLimitModal({ kind: 'source' }); return; }
    setPicker(key);
  }, [remaining]);

  const onPicked = (kind) => async (ids, files) => {
    track(ids || [], kind);
    notify(t('ac_kb_added_files', { count: (files || ids || []).length }));
    await refreshAgent();
  };

  const canAddManual = manualUrls.some((u) => u.trim().length > 0);
  const allNew = pages.length > 0 && sel.length === pages.length;
  const someNew = sel.length > 0 && !allNew;
  const toggleSel = (l) => setSel((p) => (p.includes(l) ? p.filter((x) => x !== l) : [...p, l]));
  const toggleAll = () => setSel(allNew ? [] : pages);

  return (
    <>
      <header className="ac-head">
        <h2 className="ac-title">{t('ac_kb_title')}</h2>
        <p className="ac-sub">{hideWebsiteSources ? t('ac_kb_sub_webflow') : t('ac_kb_sub')}</p>
      </header>

      <div className="ac-body">
        {(
          <>
            {!hideWebsiteSources && (
              <section className="ac-section">
                <div className="ac-section-head">
                  <h3 className="ac-section-title"><FontAwesomeIcon icon={faGlobe} /> {t('ac_kb_web_title')}</h3>
                  {countByType.url_link > 0 && <span className="ac-pill-ok"><FontAwesomeIcon icon={faCheck} /> {t('ac_kb_pages_added', { count: countByType.url_link })}</span>}
                  <div className="ac-seg" role="tablist">
                    <button type="button" role="tab" aria-selected={mode === 'find'} className={mode === 'find' ? 'is-on' : ''} onClick={() => setMode('find')}>{t('ac_kb_mode_find')}</button>
                    <button type="button" role="tab" aria-selected={mode === 'manual'} className={mode === 'manual' ? 'is-on' : ''} onClick={() => setMode('manual')}>{t('ac_kb_mode_manual')}</button>
                  </div>
                </div>

                {mode === 'find' ? (
                  <>
                    <div className="ac-kb-find">
                      <TextField
                        fullWidth size="small"
                        label={t('website_link_sitemap')}
                        placeholder="https://www.yoursite.com"
                        value={fetchUrl}
                        onChange={(e) => setFetchUrl(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !isFetching && fetchUrl.trim()) { setListOpen(true); discovery.startCrawl(fetchUrl); } }}
                        helperText={t('sitemap_hint')}
                        variant="outlined"
                        sx={FIELD_SX}
                      />
                      <button type="button" className="ac-btn-primary ac-kb-find-btn" disabled={isFetching || !fetchUrl.trim()} onClick={() => { setListOpen(true); discovery.startCrawl(fetchUrl); }}>
                        {isFetching ? <span className="ac-spinner ac-spinner-sm ac-spinner-light" /> : <FontAwesomeIcon icon={faMagnifyingGlass} />}
                        {isFetching ? t('ac_kb_finding') : t('ac_kb_find_btn')}
                      </button>
                    </div>

                    {isFetching && <Radar text={t(`ac_kb_scan_${scanStep + 1}`)} />}

                    {!isFetching && !!errorMsg && <div className="ac-error">{errorMsg}</div>}

                    {!isFetching && pages.length > 0 && (
                      <>
                        {restored && (
                          <div className="ac-restored"><FontAwesomeIcon icon={faRotateLeft} /> {t('ac_kb_restored', { count: pages.length })}</div>
                        )}
                        <div className={`ac-lg ${listOpen ? 'is-open' : ''}`}>
                          <div className="ac-lg-head" role="button" tabIndex={0} onClick={() => setListOpen((o) => !o)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setListOpen((o) => !o); } }}>
                            <FontAwesomeIcon icon={faChevronDown} className="ac-lg-chev" />
                            <span className="ac-lg-icon"><FontAwesomeIcon icon={faLightbulb} /></span>
                            <span className="ac-lg-title">{t('ac_kb_group_new')} <span className="ac-lg-count">({pages.length})</span></span>
                            <span className="ac-lg-sel">{t('n_selected', { count: sel.length })}</span>
                            <button type="button" className={`ac-btn-primary ac-btn-sm ${restored ? 'ac-attention' : ''}`} disabled={!sel.length || busy}
                              onClick={(e) => { e.stopPropagation(); addUrls(sel, { fromDiscovery: true }); }}>
                              {busy ? <span className="ac-spinner ac-spinner-sm ac-spinner-light" /> : <FontAwesomeIcon icon={faPlus} />} {t('ac_kb_add_selected')}
                            </button>
                          </div>
                          {listOpen && (
                            <div className="ac-lg-body">
                              <label className="ac-lg-row ac-lg-row-all">
                                <Checkbox size="small" checked={allNew} indeterminate={someNew} onChange={toggleAll} sx={CHECK_SX} />
                                <span>{t('select_all')}</span>
                              </label>
                              <div className="ac-lg-list">
                                {pages.map((l) => (
                                  <label key={l} className="ac-lg-row">
                                    <Checkbox size="small" checked={sel.includes(l)} onChange={() => toggleSel(l)} sx={CHECK_SX} />
                                    <span className="ac-lg-url" title={l}>{l}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {!isFetching && !pages.length && !errorMsg && justAdded > 0 && (
                      <div className="ac-pill-ok ac-kb-added-line"><FontAwesomeIcon icon={faCircleCheck} /> {t('ac_kb_added_line', { count: justAdded })}</div>
                    )}
                  </>
                ) : (
                  <div className="ac-kb-manual">
                    {manualUrls.map((url, idx) => (
                      <div key={idx} className="ac-kb-manual-row">
                        <TextField fullWidth size="small"
                          label={`${t('website_link')}${manualUrls.length > 1 ? ` ${idx + 1}` : ''}`}
                          placeholder="https://www.yoursite.com/pricing"
                          value={url}
                          onChange={(e) => setManualUrls((p) => p.map((u, i) => (i === idx ? e.target.value : u)))}
                          onKeyDown={(e) => { if (e.key === 'Enter' && canAddManual && !busy) addUrls(manualUrls); }}
                          variant="outlined" sx={FIELD_SX} />
                        {manualUrls.length > 1 && (
                          <button type="button" className="ac-icon-btn" title={t('remove')} onClick={() => setManualUrls((p) => p.filter((_, i) => i !== idx))}>
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        )}
                      </div>
                    ))}
                    <div className="ac-kb-manual-actions">
                      <button type="button" className="ac-link-btn" onClick={() => setManualUrls((p) => [...p, ''])}><FontAwesomeIcon icon={faPlus} /> {t('add_link')}</button>
                      <button type="button" className="ac-btn-primary ac-btn-sm" disabled={!canAddManual || busy} onClick={() => addUrls(manualUrls)}>
                        {busy ? <span className="ac-spinner ac-spinner-sm ac-spinner-light" /> : <FontAwesomeIcon icon={faPlus} />} {t('ac_kb_add_pages')}
                      </button>
                    </div>
                  </div>
                )}
              </section>
            )}

            <section className="ac-section">
              <h3 className="ac-section-title"><FontAwesomeIcon icon={faFileLines} /> {hideWebsiteSources ? t('ac_kb_sources_title_webflow') : t('ac_kb_sources_title')}</h3>
              <div className="ac-src-grid">
                {SOURCES.map((s) => {
                  const n = countByType[s.type] || 0;
                  return (
                    <button key={s.key} type="button" className={`ac-src ${n ? 'has' : ''}`} onClick={() => openSource(s.key)} disabled={busy}>
                      <span className="ac-src-icon">{s.logo ? <img src={s.logo} alt="" /> : <FontAwesomeIcon icon={s.icon} />}</span>
                      <span className="ac-src-label">{t(s.labelKey)}</span>
                      <span className="ac-src-meta">{n ? <><FontAwesomeIcon icon={faCheck} /> {t('ac_src_n_added', { count: n })}</> : t('ac_src_add')}</span>
                    </button>
                  );
                })}
              </div>
            </section>

            {err && <div className="ac-error">{err}</div>}
          </>
        )}
        {strips}
      </div>

      <StepFooter
        onBack={allowBack ? goBack : undefined}
        primaryLabel={t('ac_kb_cta')}
        onPrimary={() => (count === 0 ? setNoKnowledgeOpen(true) : goNext())}
        left={(
          /* The Knowledge Base's own usage widget (same numbers); Upgrade opens the plan modal. */
          <KnowledgeUsageBar
            usage={{ knowledgeCount: count, knowledgeLimit: limit, isAtLimit: atLimit }}
            onUpgrade={openUpgrade}
          />
        )}
      />

      {/* Nothing added yet: the portal's own "continue without knowledge?" prompt. */}
      <KnowledgeWarningModal
        open={noKnowledgeOpen}
        onCancel={() => setNoKnowledgeOpen(false)}
        onConfirm={() => { setNoKnowledgeOpen(false); goNext(); }}
      />

      {/* Same limit modal as the AI Studio / Knowledge Base, plus "add the first N only". */}
      <KnowledgeUpgradeModal
        open={!!limitModal}
        onClose={() => setLimitModal(null)}
        bot={agent}
        currentKnowledgeCount={count + (limitModal?.urls?.length || 0)}
        description={limitModal && limitModal.kind !== 'source'
          ? t(limitModal.kind === 'files' ? 'ac_kb_limit_modal_body_files' : 'ac_kb_limit_modal_body_pages', { selected: limitModal.urls.length, remaining, plan })
          : null}
        onUpgrade={openUpgrade}
        secondaryAction={limitModal && limitModal.kind !== 'source' && remaining > 0 ? {
          label: t('ac_kb_limit_modal_first', { count: remaining }),
          onClick: () => {
            const m = limitModal; setLimitModal(null);
            if (!m) return;
            if (m.kind === 'files') doAddDocuments(m.urls.slice(0, remaining));
            else doAddUrls(m.urls.slice(0, remaining), m.fromDiscovery);
          },
        } : null}
      />
      <PlanModal
        open={planOpen}
        onClose={closePlan}
        agent={agent}
        returnPath={returnPath}
        title={atLimit ? t('ac_kb_limit_title', { limit, plan }) : t('ac_plan_modal_title')}
        subtitle={t('ac_kb_limit_sub')}
      />

      {/* The upload modal is MUI (portals itself); the connector pickers render an
          inline fixed overlay, so we portal them to <body> to cover the whole screen. */}
      <UploadFileModal open={picker === 'documents'} onClose={() => setPicker(null)} onSubmit={(urls) => { setPicker(null); addDocuments(urls); }} />
      {createPortal(
        <>
          <GoogleDrivePickerModal isOpen={picker === 'gdrive'} onClose={() => setPicker(null)} onFilesSelected={onPicked('gdrive')} botId={botId} remainingSlots={remaining} onUpgrade={openUpgrade} returnTo={returnPath} />
          <OneDrivePickerModal isOpen={picker === 'onedrive'} onClose={() => setPicker(null)} onFilesSelected={onPicked('onedrive')} botId={botId} remainingSlots={remaining} onUpgrade={openUpgrade} returnTo={returnPath} />
          <NotionPickerModal isOpen={picker === 'notion'} onClose={() => setPicker(null)} onFilesSelected={onPicked('notion')} botId={botId} remainingSlots={remaining} onUpgrade={openUpgrade} returnTo={returnPath} />
        </>,
        document.body
      )}
    </>
  );
}
